/**
 * Word-level diffing for legislative "Updates" markup.
 *
 * Given the previous and amended text of a section, produce a sequence of
 * segments: unchanged words, deletions (rendered struck-through) and insertions
 * (rendered bold-red) — matching how Lawstream presents amendments today.
 *
 * Pure and deterministic, so it's fully unit-testable with no I/O.
 */

export type DiffKind = "equal" | "delete" | "insert";

export type DiffSegment = {
  readonly kind: DiffKind;
  readonly text: string;
};

/** Split into runs of whitespace and runs of non-whitespace (word-ish tokens). */
export const tokenize = (text: string): string[] => text.match(/\s+|\S+/g) ?? [];

type IndexedToken = {
  readonly text: string;
  readonly start: number;
  readonly end: number;
  readonly whitespace: boolean;
};

const indexedTokens = (text: string): IndexedToken[] => {
  let offset = 0;
  return tokenize(text).flatMap((token) => {
    const start = offset;
    offset += token.length;
    // Pasted clause markers often lose the space before their first word.
    // Split only at line starts, retaining exact source offsets for rendering.
    // Do not strip spaces inside ordinary words, numbers or cross-references.
    const linePrefix = text.slice(text.lastIndexOf("\n", start - 1) + 1, start);
    const marker = /^\([0-9A-Za-z]+\)(?=\S)/.exec(token)?.[0];
    if (marker !== undefined && /^\s*$/.test(linePrefix)) {
      return [
        { text: marker, start, end: start + marker.length, whitespace: false },
        { text: token.slice(marker.length), start: start + marker.length, end: offset, whitespace: false },
      ];
    }
    return [{ text: token, start, end: offset, whitespace: token.trim() === "" }];
  });
};

const appendSegment = (segments: DiffSegment[], kind: DiffKind, text: string): void => {
  if (text.length > 0) segments.push({ kind, text });
};

const hasNonWhitespace = (text: string): boolean => /\S/.test(text);
const leadingWhitespaceLength = (text: string): number => text.match(/^\s+/)?.[0].length ?? 0;
const trailingWhitespaceLength = (text: string): number => text.match(/\s+$/)?.[0].length ?? 0;

const emitGap = (segments: DiffSegment[], oldGap: string, newGap: string): void => {
  if (oldGap === "" && newGap === "") return;

  const oldHasWords = hasNonWhitespace(oldGap);
  const newHasWords = hasNonWhitespace(newGap);

  if (!oldHasWords && !newHasWords) {
    appendSegment(segments, "equal", newGap);
    return;
  }

  if (oldHasWords && !newHasWords) {
    if (newGap !== "" && oldGap.startsWith(newGap)) {
      appendSegment(segments, "equal", newGap);
      appendSegment(segments, "delete", oldGap.slice(newGap.length));
    } else if (newGap !== "" && oldGap.endsWith(newGap)) {
      appendSegment(segments, "delete", oldGap.slice(0, oldGap.length - newGap.length));
      appendSegment(segments, "equal", newGap);
    } else {
      appendSegment(segments, "delete", oldGap);
      appendSegment(segments, "equal", newGap);
    }
    return;
  }

  if (!oldHasWords && newHasWords) {
    if (oldGap !== "" && newGap.startsWith(oldGap)) {
      appendSegment(segments, "equal", oldGap);
      appendSegment(segments, "insert", newGap.slice(oldGap.length));
    } else if (oldGap !== "" && newGap.endsWith(oldGap)) {
      appendSegment(segments, "insert", newGap.slice(0, newGap.length - oldGap.length));
      appendSegment(segments, "equal", oldGap);
    } else {
      appendSegment(segments, "insert", newGap);
    }
    return;
  }

  let oldRest = oldGap;
  let newRest = newGap;
  const oldLeading = leadingWhitespaceLength(oldRest);
  const newLeading = leadingWhitespaceLength(newRest);
  if (oldLeading > 0 && newLeading > 0) {
    appendSegment(segments, "equal", newRest.slice(0, newLeading));
    oldRest = oldRest.slice(oldLeading);
    newRest = newRest.slice(newLeading);
  }

  const oldTrailing = trailingWhitespaceLength(oldRest);
  const newTrailing = trailingWhitespaceLength(newRest);
  let trailingEqual = "";
  if (oldTrailing > 0 && newTrailing > 0) {
    trailingEqual = newRest.slice(newRest.length - newTrailing);
    oldRest = oldRest.slice(0, oldRest.length - oldTrailing);
    newRest = newRest.slice(0, newRest.length - newTrailing);
  }

  if (hasNonWhitespace(oldRest)) appendSegment(segments, "delete", oldRest);
  if (hasNonWhitespace(newRest)) appendSegment(segments, "insert", newRest);
  else appendSegment(segments, "equal", newRest);
  appendSegment(segments, "equal", trailingEqual);
};

/**
 * Longest-common-subsequence word diff. O(n*m) - fine for section-sized text.
 * Whitespace-only differences are treated as formatting so pasted line wraps do
 * not hide the actual inserted/deleted legislative words.
 */
export const diffWords = (oldText: string, newText: string): DiffSegment[] => {
  const a = indexedTokens(oldText).filter((token) => !token.whitespace);
  const b = indexedTokens(newText).filter((token) => !token.whitespace);
  const n = a.length;
  const m = b.length;

  // dp[i][j] = LCS length of a[i..] and b[j..]
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  );
  const cell = (row: number, col: number): number => dp[row]?.[col] ?? 0;

  for (let i = n - 1; i >= 0; i--) {
    const row = dp[i];
    const oldToken = a[i];
    if (row === undefined || oldToken === undefined) continue;

    for (let j = m - 1; j >= 0; j--) {
      const newToken = b[j];
      if (newToken === undefined) continue;
      row[j] = oldToken.text === newToken.text
        ? cell(i + 1, j + 1) + 1
        : Math.max(cell(i + 1, j), cell(i, j + 1));
    }
  }

  const matches: Array<readonly [IndexedToken, IndexedToken]> = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    const oldToken = a[i];
    const newToken = b[j];
    if (oldToken === undefined || newToken === undefined) break;

    if (oldToken.text === newToken.text) {
      matches.push([oldToken, newToken]);
      i++;
      j++;
    } else if (cell(i + 1, j) >= cell(i, j + 1)) {
      i++;
    } else {
      j++;
    }
  }

  const raw: DiffSegment[] = [];
  let oldCursor = 0;
  let newCursor = 0;
  for (const [oldToken, newToken] of matches) {
    emitGap(raw, oldText.slice(oldCursor, oldToken.start), newText.slice(newCursor, newToken.start));
    appendSegment(raw, "equal", newText.slice(newToken.start, newToken.end));
    oldCursor = oldToken.end;
    newCursor = newToken.end;
  }
  emitGap(raw, oldText.slice(oldCursor), newText.slice(newCursor));

  return coalesce(raw);
};

/** Merge adjacent segments of the same kind so output is compact. */
export const coalesce = (segments: readonly DiffSegment[]): DiffSegment[] => {
  const out: DiffSegment[] = [];
  for (const seg of segments) {
    const last = out[out.length - 1];
    if (last && last.kind === seg.kind) {
      out[out.length - 1] = { kind: last.kind, text: last.text + seg.text };
    } else {
      out.push(seg);
    }
  }
  return out;
};

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/** Render segments to HTML for the Updates tab / email digests. */
export const segmentsToHtml = (segments: readonly DiffSegment[]): string =>
  segments
    .map((seg) => {
      const text = escapeHtml(seg.text);
      switch (seg.kind) {
        case "equal":
          return text;
        case "delete":
          return `<del class="lawstream-deletion">${text}</del>`;
        case "insert":
          return `<strong class="lawstream-insertion">${text}</strong>`;
      }
    })
    .join("");

/** The clean, amended text (insertions + unchanged, deletions dropped). */
export const cleanText = (segments: readonly DiffSegment[]): string =>
  segments
    .filter((s) => s.kind !== "delete")
    .map((s) => s.text)
    .join("");
